/**
 * useAutoSave -- 자동 저장 + 수동 저장 훅
 *
 * - isDirty 변경 감지 -> 5초 디바운스 후 자동 저장
 * - save() 호출 시 즉시 저장
 * - Cmd/Ctrl+S 단축키 바인딩
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useInvitationStore } from '../../stores/invitationStore.js'
import { useApiMutation } from '../../hooks/useApiFetch.js'
import { useUIStore } from '../../stores/uiStore.js'
import type { Invitation } from '@cugumi/shared/schemas'

export type SaveStatus = 'saved' | 'saving' | 'dirty' | 'error'

const AUTO_SAVE_DELAY = 5000

// InvitationDataSchema shape에서 키 목록 추출 — 저장 시 top-level에서 data로 다시 모음
import { InvitationDataSchema } from '@cugumi/shared/schemas'
const DATA_KEYS = Object.keys(InvitationDataSchema._def.innerType.shape)

function buildSavePayload(inv: Record<string, unknown>) {
  const data: Record<string, unknown> = {}
  for (const key of DATA_KEYS) {
    if (inv[key] !== undefined) data[key] = inv[key]
  }
  return {
    templateId: inv.templateId,
    slug: inv.slug ?? null,
    data,
    status: inv.status,
  }
}

export function useAutoSave(invitationId: string) {
  const invitation = useInvitationStore((s) => s.invitation)
  const isDirty = useInvitationStore((s) => s.isDirty)
  const resetDirty = useInvitationStore((s) => s.resetDirty)
  const updateField = useInvitationStore((s) => s.updateField)
  const addToast = useUIStore((s) => s.addToast)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const { trigger } = useApiMutation<Invitation>(
    `/api/invitations/${invitationId}`,
    'PUT',
    { invalidateKeys: [[`/api/invitations/${invitationId}`], ['/api/invitations']] },
  )

  const save = useCallback(async () => {
    if (!invitation || !isDirty) return

    if (timerRef.current) clearTimeout(timerRef.current)

    setSaveStatus('saving')
    const payload = buildSavePayload(invitation as unknown as Record<string, unknown>)
    const result = await trigger(payload)

    if (result) {
      resetDirty()
      setSaveStatus('saved')
    } else {
      setSaveStatus('error')
      addToast({ type: 'error', message: '저장에 실패했습니다' })
    }
  }, [invitation, isDirty, trigger, resetDirty, addToast])

  const publish = useCallback(async () => {
    if (!invitation) return

    if (timerRef.current) clearTimeout(timerRef.current)

    setSaveStatus('saving')
    const payload = {
      ...buildSavePayload(invitation as unknown as Record<string, unknown>),
      status: 'PUBLISHED',
    }
    const result = await trigger(payload)

    if (result) {
      updateField('status', 'PUBLISHED')
      resetDirty()
      setSaveStatus('saved')
      addToast({ type: 'success', message: '청첩장이 발행되었습니다' })
    } else {
      setSaveStatus('error')
      addToast({ type: 'error', message: '발행에 실패했습니다' })
    }
  }, [invitation, trigger, updateField, resetDirty, addToast])

  const saveRef = useRef(save)
  useEffect(() => { saveRef.current = save }, [save])

  // isDirty 변경 -> 자동 저장 디바운스
  useEffect(() => {
    if (!isDirty) return

    setSaveStatus('dirty')
    timerRef.current = setTimeout(() => {
      saveRef.current()
    }, AUTO_SAVE_DELAY)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [isDirty, invitation])

  // Cmd/Ctrl+S 단축키
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        saveRef.current()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return { saveStatus, save, publish }
}
